import formatFileSize from 'zdocs-web-core/lib/filesize';
import { RejectionErrorCode, FileRejection } from './error';
import { ErrorCode, FileUploadStatus, ZDocsUpload } from './upload-manager';

export enum FileUploadGroupType {
  Rejected,
  Pending,
  Processing,
  Failed,
  Completed,
}

type BaseFileUpload<T extends FileUploadGroupType> = {
  id: number;
  groupType: T;
  file: File;
  status: FileUploadStatus;
  statusDescription: string;
  canDelete: boolean;
};

type BaseFileUploadGroup<
  T1 extends FileUploadGroupType,
  T2 extends BaseFileUpload<T1>
> = {
  groupType: T1;
  groupFiles: Array<T2>;
  description: string;
};

export type RejectedFileUpload =
  BaseFileUpload<FileUploadGroupType.Rejected> & {
    status: FileUploadStatus.Rejected;
    errorCode: RejectionErrorCode | ErrorCode | string;
    canDelete: true;
  };

export type RejectedFileUploadGroup = BaseFileUploadGroup<
  FileUploadGroupType.Rejected,
  RejectedFileUpload
>;

export type PendingFileUpload = BaseFileUpload<FileUploadGroupType.Pending> & {
  status: FileUploadStatus.Pending;
  progress: 0;
  canDelete: true;
};

export type PendingFileUploadGroup = BaseFileUploadGroup<
  FileUploadGroupType.Pending,
  PendingFileUpload
>;

export type ProcessingFileUpload =
  BaseFileUpload<FileUploadGroupType.Processing> & {
    status: FileUploadStatus;
    progress: number;
    errorCode: ErrorCode | string | null;
    canDelete: false;
  };

export type ProcessingFileUploadGroup = BaseFileUploadGroup<
  FileUploadGroupType.Processing,
  ProcessingFileUpload
>;

export type FailedFileUpload = BaseFileUpload<FileUploadGroupType.Failed> & {
  status: FileUploadStatus.Failed;
  progress: 100;
  canDelete: true;
};

export type FailedFileUploadGroup = BaseFileUploadGroup<
  FileUploadGroupType.Failed,
  FailedFileUpload
>;

export type CompletedFileUpload =
  BaseFileUpload<FileUploadGroupType.Completed> & {
    status: FileUploadStatus.Completed;
    progress: 100;
    canDelete: false;
  };

export type CompletedFileUploadGroup = BaseFileUploadGroup<
  FileUploadGroupType.Completed,
  CompletedFileUpload
>;

export type FileUpload =
  | RejectedFileUpload
  | PendingFileUpload
  | ProcessingFileUpload
  | FailedFileUpload
  | CompletedFileUpload;

export type FileUploadGroup =
  | RejectedFileUploadGroup
  | PendingFileUploadGroup
  | ProcessingFileUploadGroup
  | FailedFileUploadGroup
  | CompletedFileUploadGroup;

export type ProcessableFileUploadGroups = {
  pendingFiles: Array<PendingFileUpload>;
  processingFiles: Array<ProcessingFileUpload>;
  failedFiles: Array<FailedFileUpload>;
  completedFiles: Array<CompletedFileUpload>;
};
export type FileUploadGroups = {
  rejectedFiles: Array<RejectedFileUpload>;
} & ProcessableFileUploadGroups;

function getClientErrorDescription(
  errorCode: RejectionErrorCode,
  maxFileSize: number
) {
  switch (errorCode) {
    case RejectionErrorCode.InvalidType:
      return 'Unsupported file type';
    case RejectionErrorCode.ExceededMaxSize:
      return `File exceeds ${formatFileSize(maxFileSize)} size limit`;
    case RejectionErrorCode.ExceededMaxFiles:
      return 'Maximum number of files has been exceeded';
    default:
      return 'File rejected';
  }
}

function getServerErrorDescription(errorCode: ErrorCode | string | null) {
  if (errorCode === ErrorCode.EncryptedPdf) {
    return 'Encrypted or corrupted PDF';
  }

  return 'File rejected';
}

function getProcessingStatusDescription(upload: ZDocsUpload) {
  const {
    status,
    progress,
    errorCode,
    file: { size: fileSize },
  } = upload;

  switch (status) {
    case FileUploadStatus.Pending:
      return 'Waiting...';
    case FileUploadStatus.Uploading:
      return `${progress}% Complete`;
    case FileUploadStatus.Completed:
      return formatFileSize(fileSize);
    case FileUploadStatus.Rejected:
      return getServerErrorDescription(errorCode);
    case FileUploadStatus.Failed:
      return 'Retry upload or delete';
  }
}

export function createClientRejectedFileUpload(
  id: number,
  fileRejection: FileRejection,
  maxFileSize: number
): RejectedFileUpload {
  const {
    file,
    errorCodes: [errorCode],
  } = fileRejection;
  const statusDescription = getClientErrorDescription(errorCode, maxFileSize);
  return {
    id,
    file,
    groupType: FileUploadGroupType.Rejected,
    status: FileUploadStatus.Rejected,
    statusDescription,
    errorCode,
    canDelete: true,
  };
}

export function createServerRejectedFileUpload(
  id: number,
  upload: ZDocsUpload
): RejectedFileUpload {
  const { file, errorCode } = upload;
  const statusDescription = getServerErrorDescription(errorCode);
  return {
    id,
    file,
    groupType: FileUploadGroupType.Rejected,
    status: FileUploadStatus.Rejected,
    statusDescription,
    errorCode: errorCode || ErrorCode.Upload,
    canDelete: true,
  };
}

export function createPendingFileUpload(
  id: number,
  file: File
): PendingFileUpload {
  return {
    id,
    file,
    groupType: FileUploadGroupType.Pending,
    status: FileUploadStatus.Pending,
    statusDescription: formatFileSize(file.size),
    progress: 0,
    canDelete: true,
  };
}

export function createProcessingFileUpload(
  id: number,
  upload: ZDocsUpload
): ProcessingFileUpload {
  const { status, progress, file, errorCode } = upload;
  const statusDescription = getProcessingStatusDescription(upload);
  return {
    id,
    file,
    groupType: FileUploadGroupType.Processing,
    status,
    statusDescription,
    progress,
    errorCode,
    canDelete: false,
  };
}

export function createFailedFileUpload(
  id: number,
  upload: ZDocsUpload
): FailedFileUpload {
  const status = FileUploadStatus.Failed;
  const progress = 100;
  const { file } = upload;
  const statusDescription = getProcessingStatusDescription(upload);
  return {
    id,
    file,
    groupType: FileUploadGroupType.Failed,
    status,
    statusDescription,
    progress,
    canDelete: true,
  };
}

export function createCompletedFileUpload(
  id: number,
  upload: ZDocsUpload
): CompletedFileUpload {
  const status = FileUploadStatus.Completed;
  const progress = 100;
  const { file } = upload;
  const statusDescription = getProcessingStatusDescription(upload);
  return {
    id,
    file,
    groupType: FileUploadGroupType.Completed,
    status,
    statusDescription,
    progress,
    canDelete: false,
  };
}

export function toPendingFileUpload(fileUpload: FileUpload): PendingFileUpload {
  return {
    ...fileUpload,
    groupType: FileUploadGroupType.Pending,
    status: FileUploadStatus.Pending,
    statusDescription: formatFileSize(fileUpload.file.size),
    progress: 0,
    canDelete: true,
  };
}

export function getFilesInflection(fileCount: number) {
  return fileCount === 1 ? 'file' : 'files';
}

export function getUploadsInflection(fileCount: number) {
  return fileCount === 1 ? 'upload' : 'uploads';
}

function getFileUploadGroupDescription(
  groupType: FileUploadGroupType,
  uploadCount: number
) {
  const fileText = getFilesInflection(uploadCount);
  const uploadText = getUploadsInflection(uploadCount);

  switch (groupType) {
    case FileUploadGroupType.Rejected:
      return `Cannot upload ${uploadCount} ${fileText}`;
    case FileUploadGroupType.Pending:
      return `${uploadCount} ${fileText} pending`;
    case FileUploadGroupType.Processing:
      return `Uploading ${uploadCount} ${fileText}`;
    case FileUploadGroupType.Failed:
      return `${uploadCount} ${uploadText} failed`;
    case FileUploadGroupType.Completed:
      return `${uploadCount} ${uploadText} complete`;
  }
}

export function createRejectedFileUploadGroup(
  groupFiles: Array<RejectedFileUpload>
): RejectedFileUploadGroup {
  const groupType = FileUploadGroupType.Rejected;
  return {
    groupType,
    groupFiles,
    description: getFileUploadGroupDescription(groupType, groupFiles.length),
  };
}

export function createPendingFileUploadGroup(
  groupFiles: Array<PendingFileUpload>
): PendingFileUploadGroup {
  const groupType = FileUploadGroupType.Pending;
  return {
    groupType,
    groupFiles,
    description: getFileUploadGroupDescription(groupType, groupFiles.length),
  };
}

export function createProcessingFileUploadGroup(
  groupFiles: Array<ProcessingFileUpload>
): ProcessingFileUploadGroup {
  const groupType = FileUploadGroupType.Processing;
  return {
    groupType,
    groupFiles,
    description: getFileUploadGroupDescription(groupType, groupFiles.length),
  };
}

export function createFailedFileUploadGroup(
  groupFiles: Array<FailedFileUpload>
): FailedFileUploadGroup {
  const groupType = FileUploadGroupType.Failed;
  return {
    groupType,
    groupFiles,
    description: getFileUploadGroupDescription(groupType, groupFiles.length),
  };
}

export function createCompletedFileUploadGroup(
  groupFiles: Array<CompletedFileUpload>
): CompletedFileUploadGroup {
  const groupType = FileUploadGroupType.Completed;
  return {
    groupType,
    groupFiles,
    description: getFileUploadGroupDescription(groupType, groupFiles.length),
  };
}
